import React, { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import dayjs, { Dayjs } from 'dayjs'
import { getBasketStock, getStock, noStock } from '../redux/actions/products'
import { arrayUnique, findCurrentPlan, getSkus } from '../utils/utils'
import {
  getUser,
  getUserOrders,
  deleteUser,
  getUserBalance,
  finishSessionCheck,
} from '../redux/actions/user'
import { getPromotions, setFuturePrice } from '../redux/actions/promotions'
import { getParameters } from '../redux/actions/parameters'
import { getCountries } from '../redux/actions/countries'
import {
  getInProgressContractCSUB,
  getInProgressContractSUB,
} from '../redux/actions/contract'
import {
  customWarehouse,
  isSubscription,
  LOCAL_STORAGE_KEYS,
  SAVED_END_DATE,
  SAVED_START_DATE,
} from '../utils/constants'
import { useRouter } from 'next/router'
import { getAssociatedProducts } from '../managers/product/packManager'
import { useCart } from '../hooks/useCart'
import { RootState } from '../redux/reducers/rootReducer'
import { GetMapDispatchTypes } from '../interfaces/common/redux'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import isBetween from 'dayjs/plugin/isBetween'
import { getTakeBackStock } from '../redux/actions/takeBack'
import { checkWishListExistence } from '../redux/actions/wishList'
import { ERequestUrl } from '../redux/helpers/requests'
import { isNonBusinessDay, getMinimumStartDate } from '../utils/businessDays'
import { isValid } from 'date-fns'

dayjs.extend(customParseFormat)
dayjs.extend(isBetween)

const resolveMinimumStartingDays = (
  storeMinimumStartingDays?: number,
  parameterMinimumStartingDays?: number
) =>
  storeMinimumStartingDays
    ? storeMinimumStartingDays
    : parameterMinimumStartingDays
    ? parameterMinimumStartingDays
    : 7

const resolveMinimumLeasingDays = (
  storeMinimumLeasingDays?: number,
  parameterMinimumLeasingDays?: number
) =>
  storeMinimumLeasingDays
    ? storeMinimumLeasingDays
    : parameterMinimumLeasingDays
    ? parseInt(`${parameterMinimumLeasingDays}`)
    : 2

const getStartDateWithBlockedRange = (
  minimumStartingDays: number,
  isSaturdayABusinessDay: boolean | undefined,
  publicHolidays: Set<string>,
  disableSundayFromCms: boolean | undefined,
  blockedStart?: string,
  blockedEnd?: string
) => {
  const minimumStartDate = getMinimumStartDate(
    minimumStartingDays,
    isSaturdayABusinessDay,
    publicHolidays,
    disableSundayFromCms
  )
  let startDate = minimumStartDate

  if (blockedStart && blockedEnd) {
    const blocked_start = dayjs(blockedStart)
    const blocked_end = dayjs(blockedEnd)
    if (startDate.isBetween(blocked_start, blocked_end, null, '[]')) {
      startDate = blocked_end.add(1, 'day')
      while (
        isNonBusinessDay(
          startDate,
          isSaturdayABusinessDay,
          publicHolidays,
          disableSundayFromCms
        )
      ) {
        startDate = startDate.add(1, 'day')
      }
      if (startDate.isBefore(minimumStartDate, 'day')) {
        startDate = minimumStartDate
      }
    }
  }

  return startDate
}

const getEndDate = (startDate: Dayjs, minimumLeasingDays: number) =>
  dayjs(startDate)
    .add(minimumLeasingDays - 1, 'day')
    .toDate()

const getStartAndEndDate = (
  minimumStartingDays: number,
  minimumLeasingDays: number,
  isSaturdayABusinessDay: boolean | undefined,
  publicHolidays: Set<string>,
  disableSundayFromCms: boolean | undefined,
  blockedStart?: string,
  blockedEnd?: string
) => {
  const startDate = getStartDateWithBlockedRange(
    minimumStartingDays,
    isSaturdayABusinessDay,
    publicHolidays,
    disableSundayFromCms,
    blockedStart,
    blockedEnd
  )
  const endDate = getEndDate(startDate, minimumLeasingDays)
  return { startDate, endDate }
}

type Tprops = ReturnType<typeof mapStateToProps> &
  GetMapDispatchTypes<typeof mapDispatchToProps> & {
    children?: any
  }

const mapStateToProps = (state: RootState) => ({
  cart: state.cart,
  products: state.products,
  product: state.product,
  parameters: state.parameters,
  packs: state.packs,
  user: state.user,
  promotions: state.promotions,
  stores: state.stores,
  contract: state.contract,
  prismic: state.prismic,
})

const mapDispatchToProps = {
  getBasketStock: getBasketStock,
  getParameters: getParameters,
  getStock: getStock,
  getUserOrders: getUserOrders,
  setFuturePrice: setFuturePrice,
  getUser: getUser,
  finishSessionCheck: finishSessionCheck,
  deleteUser: deleteUser,
  getPromotions: getPromotions,
  getCountries: getCountries,
  getInProgressContractCSUB: getInProgressContractCSUB,
  getInProgressContractSUB: getInProgressContractSUB,
  getUserBalance: getUserBalance,
  getTakeBackStock: getTakeBackStock,
  noStock: noStock,
  checkWishListExistence: checkWishListExistence,
}

const CartManager = ({
  children,
  packs,
  getBasketStock,
  getUserOrders,
  getUser,
  finishSessionCheck,
  getPromotions,
  deleteUser,
  setFuturePrice,
  getParameters,
  getInProgressContractCSUB,
  getInProgressContractSUB,
  checkWishListExistence,
  stores,
  parameters,
  getCountries,
  promotions,
  getStock,
  getTakeBackStock,
  noStock,
  user,
  product,
  products,
  cart,
  prismic,
  getUserBalance,
}: Tprops) => {
  const {
    deleteCart,
    createCart,
    createSubscriptionCart,
    getCart,
    updateCartSubscription,
  } = useCart()
  // Hook used to check if component is already mounted, to prevent it to call each useEffect hooks on all rendering
  const didMountProducts = useRef(false)
  const didMountProduct = useRef(false)
  const didMountPacks = useRef(false)
  const [publicHolidays, setPublicHolidays] = useState<Set<string>>(new Set())
  const [holidaysLoaded, setHolidaysLoaded] = useState(false)
  const router = useRouter()
  let warehouseId = stores?.selectedStore?.warehouseId
  if (!warehouseId && customWarehouse && !isNaN(+customWarehouse)) {
    warehouseId = +customWarehouse
  }

  const isEnableChooseStartingDate = parameters?.items?.enableChooseStartingDate

  const getBlockedDateRange = () =>
    isValid(new Date(prismic?.general?.blocked_dates_start)) &&
    isValid(new Date(prismic?.general?.blocked_dates_end))
      ? {
          start: new Date(prismic?.general?.blocked_dates_start),
          end: new Date(prismic?.general?.blocked_dates_end),
        }
      : undefined

  const getCustomId = () =>
    router.route === '/payment'
      ? router.asPath.match(/customId=([^&]+)/)?.[1]
      : undefined

  useEffect(() => {
    if (process.env.DISABLE_DAYSOFF !== 'false') {
      setHolidaysLoaded(true)
      return
    }

    const fetchPublicHolidays = async () => {
      try {
        const currentYear = dayjs().year()
        const [responseNow, responsePlusOne] = await Promise.all([
          fetch(`${ERequestUrl.apiGouvFr}/${currentYear}.json`),
          fetch(`${ERequestUrl.apiGouvFr}/${currentYear + 1}.json`),
        ])
        const [dataNow, dataPlusOne] = await Promise.all([
          responseNow.json(),
          responsePlusOne.json(),
        ])
        setPublicHolidays(
          new Set<string>([
            ...Object.keys(dataNow),
            ...Object.keys(dataPlusOne),
          ])
        )
      } catch (error) {
        // On fetch failure, publicHolidays stays empty and cart proceeds without holiday blocking
        console.error('Error fetching public holidays:', error)
      } finally {
        setHolidaysLoaded(true)
      }
    }

    fetchPublicHolidays()
  }, [])

  // useEffect
  useEffect(() => {
    let createCartPromise
    ;(async () => {
      if (!prismic?.general) return
      if (!holidaysLoaded) return
      if (parameters?.items) {
        const minimumStartingDays = resolveMinimumStartingDays(
          stores?.selectedStore?.minimumStartingDays,
          parameters?.items?.minimumStartingDays
        )
        const minimumLeasingDays = resolveMinimumLeasingDays(
          stores?.selectedStore?.minimumLeasingDays,
          parameters?.items?.minimumLeasingDays
        )
        const isSaturdayABusinessDay = parameters?.items?.isSaturdayABusinessDay
        const disableSundayFromCms =
          !!prismic?.general?.disable_sunday_on_date_picker
        const customId = getCustomId()

        if (
          !cart.item &&
          cart.status !== 'cart deleted' &&
          cart.status !== 'error'
        ) {
          if (localStorage.getItem('CartToken') || customId) {
            getCart(customId, getBlockedDateRange())
          } else if (cart.status !== 'cart deleted for DP') {
            if (!isSubscription || isEnableChooseStartingDate) {
              const { startDate, endDate } = getStartAndEndDate(
                minimumStartingDays,
                minimumLeasingDays,
                isSaturdayABusinessDay,
                publicHolidays,
                disableSundayFromCms,
                prismic?.general?.blocked_dates_start,
                prismic?.general?.blocked_dates_end
              )
              createCartPromise = setTimeout(
                () => createCart(dayjs(startDate), dayjs(endDate), warehouseId),
                700
              )
            } else {
              const startSubscriptionDate = getMinimumStartDate(
                minimumStartingDays,
                isSaturdayABusinessDay,
                publicHolidays,
                disableSundayFromCms
              )
              createSubscriptionCart(startSubscriptionDate, 1, warehouseId)
            }
          }
        } else if (
          (cart.status === 'error' &&
            (cart.error?.message === 'Request failed with status code 404' ||
              cart.error?.message === 'Request failed with status code 400')) ||
          cart.status === 'cart deleted'
        ) {
          // cart not found, create new one
          // remove current Cart token to save the new one
          localStorage.removeItem('CartToken')

          const savedStartDate = localStorage.getItem(`${SAVED_START_DATE}`)
          const minimumStartDate = getMinimumStartDate(
            minimumStartingDays,
            isSaturdayABusinessDay,
            publicHolidays,
            disableSundayFromCms
          )
          const useSavedDate =
            dayjs(savedStartDate, 'DD/MM/YYYY').diff(minimumStartDate, 'day') >=
            0

          if (!isSubscription) {
            const endStartDate = localStorage.getItem(`${SAVED_END_DATE}`)
            const startDate = minimumStartDate
            const endDate = dayjs(startDate)
              .add(minimumLeasingDays - 1, 'day')
              .toDate()
            createCartPromise = setTimeout(
              () =>
                createCart(
                  savedStartDate && useSavedDate
                    ? dayjs(savedStartDate, 'DD/MM/YYYY')
                    : startDate,
                  endStartDate && useSavedDate
                    ? dayjs(endStartDate, 'DD/MM/YYYY')
                    : dayjs(endDate),
                  warehouseId
                ),
              700
            )
          } else {
            const startDate = minimumStartDate
            createSubscriptionCart(
              savedStartDate && useSavedDate
                ? dayjs(savedStartDate, 'DD/MM/YYYY')
                : dayjs(startDate),
              1,
              warehouseId
            )
          }
        }
      }
      if (
        cart?.item &&
        cart.status === 'cart live' &&
        !localStorage.getItem('CartToken')
      ) {
        localStorage.setItem('CartToken', cart.item.tokenValue)
      }
    })()
    return () => {
      if (createCartPromise) {
        clearTimeout(createCartPromise)
      }
    }
  }, [
    cart.item,
    cart.status,
    holidaysLoaded,
    publicHolidays,
    parameters?.items,
    prismic?.general,
    stores?.selectedStore,
  ])

  // Delete cart if locale change
  useEffect(() => {
    if (
      cart?.item?.tokenValue &&
      cart.status === 'cart live' &&
      !cart.loading &&
      cart.item.locale.substring(0, 2) !== router.locale
    ) {
      deleteCart()
    }
  }, [router.locale, cart?.item?.tokenValue])

  // Update a subscription cart to receive informations about price changes because of current subscription
  useEffect(() => {
    // We need to be in subscription mode, we need a cart and a user, and a specific cart state (shipping method not currently selected)
    if (
      cart?.item?.tokenValue &&
      user?.user?.id &&
      cart?.item?.checkoutState === 'cart' &&
      !cart?.isUpdated
    ) {
      updateCartSubscription()
    }
  }, [cart?.item?.tokenValue, user?.user?.id])

  // Products Stock Management
  useEffect(() => {
    if (didMountProducts.current === true) {
      const _products =
        products?.items?.items && products?.items?.items?.length > 0
          ? products?.items?.items
          : products?.wishList?.items || []
      if (
        _products?.length > 0 &&
        products.loading === false &&
        cart.status === 'cart live'
      ) {
        // if we use cumulative products, we don't need to find the stock for the products that has already loaded
        // so we cut the current product stock we have frome the array of SKUs to get only the one we charge
        let SKUArray = []
        if (products?.isCumulative) {
          let newArray = []
          newArray = [..._products]
          const limit = products.items.limit * (products.items.page - 1)
          if (products.items.page > 1) {
            newArray.splice(0, limit)
          }
          SKUArray = getSkus(newArray)
        } else {
          SKUArray = getSkus(_products)
        }

        if (
          cart.item?.startLeasingDate &&
          cart.item?.endLeasingDate &&
          !isSubscription
        ) {
          if (SKUArray.length > 0) {
            const data = {
              lizeeSKUs: SKUArray,
              from: dayjs(cart.item?.startLeasingDate, 'DD/MM/YYYY').format(
                'YYYY-MM-DD'
              ),
              to: dayjs(cart.item?.endLeasingDate, 'DD/MM/YYYY').format(
                'YYYY-MM-DD'
              ),
            }
            getStock(data, warehouseId)
            getTakeBackStock(data)
            prismic?.general?.wishlist_enabled &&
              checkWishListExistence(_products.map((p) => p.id))
          }
        } else {
          const SKUArray = getSkus(_products)
          if (SKUArray.length > 0) {
            const data = {
              lizeeSKUs: SKUArray,
            }
            getStock(data, warehouseId)
            getTakeBackStock(data)
            prismic?.general?.wishlist_enabled &&
              checkWishListExistence(_products.map((p) => p.id))
          }
        }
      }
    } else {
      didMountProducts.current = true
      noStock()
    }
  }, [
    didMountProducts,
    products.items,
    products.wishList?.items,
    cart.item?.startLeasingDate,
    cart.item?.endLeasingDate,
  ])

  // Basket Stock Management
  useEffect(() => {
    if (cart?.item && cart.item.items?.length > 0) {
      const SKUArray = []
      cart?.item?.items?.map((item) => {
        SKUArray.push(item.product.variants[0].sku)
      })
      if (
        cart.item?.startLeasingDate &&
        cart.item?.endLeasingDate &&
        !isSubscription
      ) {
        if (SKUArray.length > 0) {
          const data = {
            lizeeSKUs: SKUArray,
            from: dayjs(cart.item?.startLeasingDate, 'DD/MM/YYYY').format(
              'YYYY-MM-DD'
            ),
            to: dayjs(cart.item?.endLeasingDate, 'DD/MM/YYYY').format(
              'YYYY-MM-DD'
            ),
          }
          getBasketStock(data, warehouseId)
        }
      } else {
        if (SKUArray.length > 0) {
          const data = {
            lizeeSKUs: SKUArray,
          }
          getBasketStock(data, warehouseId)
        }
      }
    }
  }, [
    cart?.item?.items,
    cart.item?.startLeasingDate,
    cart.item?.endLeasingDate,
    router.pathname,
  ])

  // Single product Stock Management
  useEffect(() => {
    if (didMountProduct.current === true) {
      if (
        product.item &&
        product.loading === false &&
        cart.status === 'cart live'
      ) {
        if (
          cart.item?.startLeasingDate &&
          cart.item?.endLeasingDate &&
          !isSubscription
        ) {
          const SKUArray = getSkus(product.item)
          if (SKUArray.length > 0) {
            const data = {
              lizeeSKUs: SKUArray,
              from: dayjs(cart.item?.startLeasingDate, 'DD/MM/YYYY').format(
                'YYYY-MM-DD'
              ),
              to: dayjs(cart.item?.endLeasingDate, 'DD/MM/YYYY').format(
                'YYYY-MM-DD'
              ),
            }
            getStock(data, warehouseId)
            prismic?.general?.wishlist_enabled &&
              checkWishListExistence([product.item.id])
          }
        } else {
          const SKUArray = getSkus(product.item)
          if (SKUArray.length > 0) {
            const data = {
              lizeeSKUs: SKUArray,
            }
            getStock(data, warehouseId)
            prismic?.general?.wishlist_enabled &&
              checkWishListExistence([product.item.id])
          }
        }
      }
    } else didMountProduct.current = true
  }, [product.item, cart.item?.startLeasingDate, cart.item?.endLeasingDate])

  // Pack Stock Management
  useEffect(() => {
    if (didMountPacks.current === true) {
      if (
        packs?.items?.length > 0 &&
        packs.loading === false &&
        cart.status === 'cart live'
      ) {
        if (
          cart.item?.startLeasingDate &&
          cart.item?.endLeasingDate &&
          !isSubscription
        ) {
          let data = {}
          let SKUArray = []
          packs?.items?.map((pack) => {
            const SKUs = getSkus(pack.packProducts)
            const listAssociatedItems = getAssociatedProducts(
              pack?.productsAssociations
            )
            const ProductsSKUs = getSkus(listAssociatedItems)
            if (SKUArray.length > 0) {
              if (ProductsSKUs.length > 0) {
                const SKUsArray = []
                SKUsArray.push(...SKUs, ...ProductsSKUs)

                SKUArray = arrayUnique(SKUArray.concat(SKUsArray))
              } else {
                SKUArray = arrayUnique(SKUArray.concat(SKUs))
              }
            } else {
              if (ProductsSKUs.length > 0) {
                const SKUsArray = []
                SKUsArray.push(...SKUs, ...ProductsSKUs)
                SKUArray = SKUsArray
              } else {
                SKUArray = SKUs
              }
            }
          })
          if (SKUArray.length > 0) {
            data = {
              lizeeSKUs: SKUArray,
              from: dayjs(cart.item?.startLeasingDate, 'DD/MM/YYYY').format(
                'YYYY-MM-DD'
              ),
              to: dayjs(cart.item?.endLeasingDate, 'DD/MM/YYYY').format(
                'YYYY-MM-DD'
              ),
            }
            getStock(data, warehouseId)
          }
        } else {
          let data = {}
          let SKUArray = []
          packs.items?.map((pack) => {
            const SKUs = getSkus(pack.packProducts)
            const listAssociatedItems = getAssociatedProducts(
              pack?.productsAssociations
            )
            const ProductsSKUs = getSkus(listAssociatedItems)
            if (SKUArray.length > 0) {
              if (ProductsSKUs.length > 0) {
                const SKUsArray = []
                SKUsArray.push(...SKUs, ...ProductsSKUs)

                SKUArray = arrayUnique(SKUArray.concat(SKUsArray))
              } else {
                SKUArray = arrayUnique(SKUArray.concat(SKUs))
              }
            } else {
              if (ProductsSKUs.length > 0) {
                const SKUsArray = []
                SKUsArray.push(...SKUs, ...ProductsSKUs)
                SKUArray = SKUsArray
              } else {
                SKUArray = SKUs
              }
            }
          })
          if (SKUArray.length > 0) {
            data = {
              lizeeSKUs: SKUArray,
            }
            getStock(data, warehouseId)
          }
        }
      }
    } else didMountPacks.current = true
  }, [packs.items, cart.item?.startLeasingDate, cart.item?.endLeasingDate])

  useEffect(() => {
    if (user?.user?.id) {
      // getUserOrders if user exists and has no order saved in state
      if (!user?.orders) {
        getUserOrders()
        if (process.env.STORE_TYPE === 'subscription') {
          const asyncFunc = async () => {
            const contract = await getInProgressContractSUB('in_progress')
            if (
              contract.type === 'GET_IN_PROGRESS_CONTRACT_SUB_FAILURE' ||
              contract.currentContract.length === 0
            ) {
              await getInProgressContractSUB('in_coming')
            }
          }
          asyncFunc()
        }
      }
      // getUserBalance if user exists and has no balance saved in state
      if (!user?.balance) {
        getUserBalance()
      }
    }
  }, [user?.user?.id])

  // handle user automatic connection
  useEffect(() => {
    const token = localStorage.getItem(LOCAL_STORAGE_KEYS.TOKEN)
    const hasToken = token != null && token !== 'null'

    if (hasToken && !user?.user?.id) {
      const restoreSession = async () => {
        const result = await getUser()
        if (result.type === 'GET_USER_FAILURE') {
          localStorage.removeItem(LOCAL_STORAGE_KEYS.TOKEN)
          localStorage.removeItem(LOCAL_STORAGE_KEYS.CONNEXION_TYPE)
          deleteUser()
        }
      }
      restoreSession()
    } else {
      finishSessionCheck()
    }
  }, [])

  // get promotions
  useEffect(() => {
    if (
      promotions?.items?.length === 0 &&
      !promotions.loading &&
      !promotions.fetched
    ) {
      getPromotions()
    }
  }, [promotions?.items, promotions.loading, promotions.fetched])

  //Futur classic subscription price if connected
  useEffect(() => {
    /*
      Here we watch 3 states : the items in current cart, the promotions
    */
    /* Here we have to check if the user exists, that he already have some orders, 
    that we are in classic_subscription, if we have some promotions and if we have products in our customer's cart */
    if (
      process.env.STORE_TYPE === 'classic_subscription' &&
      promotions?.items?.length > 0 &&
      cart?.item?.items?.length > 0
    ) {
      const asyncFunction = async () => {
        let contract = await getInProgressContractCSUB('in_progress')
        if (
          contract.type === 'GET_IN_PROGRESS_CONTRACT_SUB_FAILURE' ||
          contract?.currentContract?.length === 0
        ) {
          contract = await getInProgressContractCSUB('in_coming')
        }
        let numberInCurrent = 0
        // We go through the user order array to calcul how many products we have
        // if user connected, we check current sub
        if (contract?.currentContract?.length > 0) {
          contract.currentContract[0].orders.map((order) => {
            if (
              order.paymentState !== 'paid' &&
              order.paymentState !== 'cancelled' &&
              order.paymentState !== 'refunded'
            ) {
              order.items?.map((item) => {
                let qteReturned = 0
                item.units?.map((unit) => {
                  if (unit.isReturned) {
                    qteReturned += 1
                  }
                })
                numberInCurrent += item.quantity - qteReturned
              })
            }
          })
        } else if (cart?.item?.items?.length > 0) {
          cart?.item?.items?.map((product) => {
            numberInCurrent += product.quantity
          })
        }
        const futurPrice = 0
        // We add 1 if a client has an order and returned all the products, he currently has 0 product but futurPrice cannot be null. Therefore we setup it at one to match the first promotion.
        if (numberInCurrent === 0) {
          numberInCurrent = 1
        }
        // We check how many products we have in the current cart
        // We go through each promotions, to find wich one will be applied to the user if he choose to continue with his current subscription and the cart he is currently creating
        // Filter plans of coupon based promotions
        const futurPlan = findCurrentPlan(
          promotions,
          numberInCurrent,
          futurPrice
        )
        // set the variable in a redux state, to have it everywhere it is needed
        setFuturePrice(futurPlan)
      }

      asyncFunction()
    }
  }, [
    cart?.item?.items,
    promotions?.items,
    user?.user?.id,
    user?.orders?.length,
  ])

  // Function to destroy any cart created before today's date
  useEffect(() => {
    let createCartPromise
    if (cart?.item?.startLeasingDate && parameters?.items && holidaysLoaded) {
      const asyncFunc = async () => {
        // Check if minimumStartingDays exists in selectedStore first, otherwise use parameters
        const minimumStartingDays =
          typeof stores?.selectedStore?.minimumStartingDays !== 'undefined'
            ? stores.selectedStore.minimumStartingDays
            : typeof parameters?.items?.minimumStartingDays !== 'undefined'
            ? parameters?.items?.minimumStartingDays
            : 7
        const minimumLeasingDays =
          typeof parameters?.items?.minimumLeasingDays !== 'undefined'
            ? parseInt(`${parameters?.items?.minimumLeasingDays}`)
            : 2
        const startDate = getMinimumStartDate(
          minimumStartingDays,
          parameters?.items?.isSaturdayABusinessDay,
          publicHolidays,
          !!prismic?.general?.disable_sunday_on_date_picker
        )
        const endDate = dayjs(startDate)
          .add(minimumLeasingDays - 1, 'day')
          .toDate()

        if (
          dayjs(cart?.item.startLeasingDate, 'DD/MM/YYYY').diff(
            startDate,
            'day'
          ) < 0
        ) {
          await deleteCart()
          if (!isSubscription) {
            createCartPromise = setTimeout(
              () => createCart(dayjs(startDate), dayjs(endDate), warehouseId),
              700
            )
            await createCart(dayjs(startDate), dayjs(endDate), warehouseId)
          } else {
            await createSubscriptionCart(dayjs(startDate), 1, warehouseId)
          }
        }
      }
      asyncFunc()
    }
    return () => {
      if (createCartPromise) {
        clearTimeout(createCartPromise)
      }
    }
  }, [
    cart?.item?.startLeasingDate,
    holidaysLoaded,
    parameters?.items,
    prismic?.general?.disable_sunday_on_date_picker,
    publicHolidays,
    stores?.selectedStore,
  ])

  // Save chosen date
  useEffect(() => {
    if (cart?.item?.startLeasingDate && cart?.item?.endLeasingDate) {
      localStorage.setItem(`${SAVED_START_DATE}`, cart?.item?.startLeasingDate)
      localStorage.setItem(`${SAVED_END_DATE}`, cart?.item?.endLeasingDate)
    }
  }, [cart?.item?.startLeasingDate, cart?.item?.endLeasingDate])

  // Get parameters
  useEffect(() => {
    if (!parameters?.items) {
      getParameters()
    }
  }, [])

  // Get available shipping countries
  useEffect(() => {
    if (!parameters?.countries) {
      getCountries()
    }
  }, [])

  return <>{children}</>
}

export default connect(mapStateToProps, mapDispatchToProps)(CartManager)
